import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const categoryImages: Record<string, string> = {
    'Shoes': 'https://readdy.ai/api/search-image?query=Single%20pair%20of%20white%20minimalist%20sneakers%20on%20warm%20cream%20surface%2C%20clean%20product%20photography%20with%20soft%20natural%20lighting%2C%20isolated%20on%20neutral%20background&width=200&height=200&seq=nav-cat-shoes&orientation=squarish',
    'Hoodies/Sweaters': 'https://readdy.ai/api/search-image?query=Oatmeal%20colored%20hoodie%20folded%20neatly%20on%20wooden%20surface%2C%20minimalist%20product%20photography%20with%20warm%20natural%20light%2C%20clean%20aesthetic%20neutral%20background&width=200&height=200&seq=nav-cat-hoodies&orientation=squarish',
    'T-Shirts': 'https://readdy.ai/api/search-image?query=White%20cotton%20t-shirt%20folded%20on%20linen%20texture%20surface%2C%20minimalist%20fashion%20photography%20with%20soft%20diffused%20lighting%2C%20clean%20neutral%20background&width=200&height=200&seq=nav-cat-tshirts&orientation=squarish',
    'Jackets': 'https://readdy.ai/api/search-image?query=Tan%20denim%20jacket%20hanging%20on%20minimal%20wooden%20hanger%20against%20cream%20wall%2C%20clean%20product%20photography%20with%20warm%20natural%20lighting&width=200&height=200&seq=nav-cat-jackets&orientation=squarish',
    'Pants/Shorts': 'https://readdy.ai/api/search-image?query=Beige%20linen%20trousers%20folded%20on%20textured%20cream%20surface%2C%20minimalist%20fashion%20flatlay%20photography%20with%20soft%20natural%20lighting&width=200&height=200&seq=nav-cat-pants&orientation=squarish',
    'Headwear': 'https://readdy.ai/api/search-image?query=Simple%20baseball%20cap%20on%20warm%20beige%20surface%2C%20clean%20minimalist%20product%20photography%20with%20soft%20natural%20lighting%20neutral%20background&width=200&height=200&seq=nav-cat-headwear&orientation=squarish',
    'Sets': 'https://readdy.ai/api/search-image?query=Matching%20top%20and%20bottom%20outfit%20set%20displayed%20on%20cream%20surface%2C%20minimalist%20fashion%20photography%20with%20warm%20neutral%20tones%20soft%20lighting&width=200&height=200&seq=nav-cat-sets&orientation=squarish',
    'Underwear/Underpants': 'https://readdy.ai/api/search-image?query=Soft%20cotton%20basics%20folded%20neatly%20on%20warm%20textured%20surface%2C%20minimalist%20product%20photography%20with%20gentle%20natural%20lighting&width=200&height=200&seq=nav-cat-underwear&orientation=squarish',
    'Jersey': 'https://readdy.ai/api/search-image?query=Sports%20jersey%20folded%20on%20clean%20cream%20surface%2C%20minimalist%20athletic%20apparel%20photography%20with%20soft%20natural%20lighting%20neutral%20background&width=200&height=200&seq=nav-cat-jersey&orientation=squarish',
    'Accessories': 'https://readdy.ai/api/search-image?query=Minimalist%20watch%20and%20bracelet%20on%20warm%20beige%20surface%2C%20clean%20accessory%20photography%20with%20soft%20natural%20lighting%20elegant%20composition&width=200&height=200&seq=nav-cat-accessories&orientation=squarish',
    'Bags': 'https://readdy.ai/api/search-image?query=Tan%20leather%20crossbody%20bag%20on%20cream%20surface%2C%20minimalist%20product%20photography%20with%20warm%20natural%20lighting%20clean%20aesthetic&width=200&height=200&seq=nav-cat-bags&orientation=squarish',
    'Belts': 'https://readdy.ai/api/search-image?query=Braided%20leather%20belt%20coiled%20on%20warm%20beige%20surface%2C%20minimalist%20accessory%20photography%20with%20soft%20natural%20lighting%20clean%20background&width=200&height=200&seq=nav-cat-belts&orientation=squarish',
    'Perfumes': 'https://readdy.ai/api/search-image?query=Glass%20perfume%20bottle%20on%20cream%20surface%20with%20soft%20shadow%2C%20minimalist%20fragrance%20photography%20with%20warm%20natural%20lighting%20elegant%20composition&width=200&height=200&seq=nav-cat-perfumes&orientation=squarish',
    'Other Stuff': 'https://readdy.ai/api/search-image?query=Assorted%20lifestyle%20items%20including%20electronics%20and%20home%20goods%20on%20warm%20cream%20surface%2C%20minimalist%20product%20photography%20with%20soft%20natural%20lighting&width=200&height=200&seq=nav-cat-other&orientation=squarish',
  };

  const closeAll = () => { setMobileOpen(false); setDropdownOpen(false); };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/98 backdrop-blur-lg border-b border-background-200/60 shadow-sm'
          : 'bg-foreground-950/30 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 whitespace-nowrap" onClick={closeAll}>
          <span className={`font-heading text-xl md:text-2xl transition-colors ${scrolled ? 'text-foreground-950' : 'text-white'}`}>
            VigorBuy <span className={scrolled ? 'text-primary-500' : 'text-accent-300'}>Garden</span>
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
          >
            Home
          </Link>

          <div className="relative" onMouseEnter={() => setDropdownOpen(true)} onMouseLeave={() => setDropdownOpen(false)}>
            <button className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}>
              Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[560px] bg-background-50 rounded-2xl border border-background-200/70 py-4 max-h-[420px] overflow-y-auto shadow-lg">
                <div className="grid grid-cols-2 gap-1 px-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-background-100 transition-colors group cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors whitespace-nowrap">{cat.name}</p>
                        <p className="text-xs text-foreground-500 truncate">{cat.description}</p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/articles/best-vigorbuy-spreadsheet-finds-2026"
            className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}
          >
            Shopping Guides
          </Link>

          <a href="#articles" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}>Guides</a>
          <a href="#categories" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-foreground-950' : 'text-white/80 hover:text-white'}`}>Finds</a>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer shadow-sm"
          >
            Buy It Now
          </a>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors ${scrolled ? 'text-foreground-950' : 'text-white'}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[85vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-3">
            <Link to="/" className="flex items-center gap-2 py-3 text-foreground-950 font-semibold text-sm whitespace-nowrap" onClick={closeAll}>
              <i className="ri-home-line text-lg"></i> Home
            </Link>

            <div className="border-t border-background-200/60 pt-3">
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Shop Categories</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex flex-col items-center gap-2 p-3 bg-background-100 rounded-xl hover:bg-primary-50 transition-colors cursor-pointer"
                    onClick={closeAll}
                  >
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-background-200">
                      <img src={categoryImages[cat.name]} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="text-xs font-medium text-foreground-700 text-center leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/60 pt-3 flex flex-col gap-1">
              <Link to="/articles/best-vigorbuy-spreadsheet-finds-2026" className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 whitespace-nowrap" onClick={closeAll}>
                <i className="ri-book-open-line"></i> Shopping Guides
              </Link>
              <a href="#articles" className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 whitespace-nowrap" onClick={closeAll}>
                <i className="ri-article-line"></i> Articles
              </a>
              <a href="#categories" className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-700 whitespace-nowrap" onClick={closeAll}>
                <i className="ri-grid-line"></i> Finds
              </a>
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="mt-2 px-5 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              onClick={closeAll}
            >
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}